import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useCompany } from "@/contexts/CompanyContext";
import HeadcountDrillDialog from "@/components/HeadcountDrillDialog";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Users, UsersRound, UserPlus, UserMinus, UserRoundX,
  TrendingUp, TrendingDown, Briefcase, AlertTriangle, Clock,
  Factory, Wrench, ClipboardList, Landmark, ChevronRight,
  ArrowUpRight, ArrowDownRight, Minus, Activity, Target, Gauge,
  ShieldAlert, Grid3x3, Download, FileImage, FileText,
} from "lucide-react";
import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, LineChart, Line,
} from "recharts";
import { toast } from "sonner";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { exportDashboardPng, exportDashboardPdf } from "@/lib/dashboardSnapshot";

const LEAVE_TYPE_LABEL = {
  APOSENTADORIA_INVALIDEZ: "Aposentadoria por Invalidez",
  APOSENTADORIA_IDADE: "Aposentadoria por Idade",
  APOSENTADORIA_TEMPO: "Aposentadoria por Tempo de Contribuição",
  APOSENTADORIA_ESPECIAL: "Aposentadoria Especial",
  AUXILIO_DOENCA: "Auxílio-Doença",
  AUXILIO_ACIDENTE_TRABALHO: "Auxílio por Acidente de Trabalho",
  SALARIO_MATERNIDADE: "Salário-Maternidade",
  CARCERE: "Cárcere",
  SEM_REMUNERACAO: "Afastamento sem Remuneração",
  COM_REMUNERACAO: "Afastamento com Remuneração",
  TIPO_NAO_INFORMADO: "Tipo não informado (cadastro legado)",
};

const BUCKETS = [
  { key: "ate_15",    label: "até 15 dias",     color: "#86EFAC" },
  { key: "16_30",     label: "16 a 30 dias",    color: "#22C55E" },
  { key: "31_60",     label: "31 a 60 dias",    color: "#FDE68A" },
  { key: "61_90",     label: "61 a 90 dias",    color: "#FBBF24" },
  { key: "91_180",    label: "91 a 180 dias",   color: "#F59E0B" },
  { key: "acima_180", label: "acima de 180 dias", color: "#F87171" },
  { key: "sem_data",  label: "sem data",        color: "#94A3B8" },
];

const OP_META = {
  Producao:       { icon: Factory,        label: "PRODUÇÃO" },
  Processo:       { icon: ClipboardList,  label: "PROCESSO" },
  Manutencao:     { icon: Wrench,         label: "MANUTENÇÃO" },
  Administrativo: { icon: Landmark,       label: "ADMINISTRAÇÃO" },
  SEM_CLASSIFICACAO: { icon: AlertTriangle, label: "SEM CLASSIFICAÇÃO" },
};

const nfmt = (n) => (n ?? 0).toLocaleString("pt-BR");

// Retorna o "AAAA-MM" do mês anterior ao mês atual.
function prevPeriodStr() {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

// ---------------------------------------------------------------------------
//  Trend badge (↑ x% vs. mês anterior)
// ---------------------------------------------------------------------------
function TrendBadge({ current, previous, unit = "%", format = "pct", light = false }) {
  if (previous === null || previous === undefined) return null;
  if (previous === 0 && current === 0) return null;
  let delta, pctDelta;
  if (format === "pp") {
    // pontos percentuais (para taxas): delta absoluta
    delta = current - previous;
    pctDelta = delta;
  } else {
    delta = current - previous;
    pctDelta = previous === 0 ? null : ((current - previous) / Math.abs(previous)) * 100;
  }
  if (pctDelta === null || !isFinite(pctDelta)) return null;
  const up = pctDelta > 0.05;
  const down = pctDelta < -0.05;
  const Icon = up ? ArrowUpRight : down ? ArrowDownRight : Minus;
  const cls = light
    ? (up ? "text-emerald-200/95" : down ? "text-rose-200/95" : "text-emerald-100/80")
    : (up ? "text-emerald-600" : down ? "text-rose-600" : "text-slate-500");
  const abs = Math.abs(pctDelta);
  const suf = format === "pp" ? " p.p." : "%";
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-medium ${cls}`}>
      <Icon className="h-3 w-3" />
      {abs.toFixed(1)}{suf} vs. mês anterior
    </span>
  );
}

// ---------------------------------------------------------------------------
//  Building blocks
// ---------------------------------------------------------------------------
function KpiCard({ label, value, hint, icon: Icon, variant = "default", onClick, testId, trend }) {
  const styles = {
    default: {
      wrap: "bg-white border-slate-200 hover:border-[#065F46]/40",
      label: "text-slate-500",
      value: "text-slate-900",
      iconBox: "bg-[#065F46] text-white",
      hint: "text-slate-500",
    },
    accent: {
      wrap: "bg-white border-slate-200 hover:border-[#C99328]/40",
      label: "text-slate-500",
      value: "text-slate-900",
      iconBox: "bg-[#F5F1E6] text-[#8A6B2B]",
      hint: "text-slate-500",
    },
    hero: {
      wrap: "bg-gradient-to-br from-[#064E3B] to-[#065F46] border-[#064E3B] shadow-[0_8px_24px_-12px_rgba(6,78,59,0.55)]",
      label: "text-emerald-100/80",
      value: "text-white",
      iconBox: "bg-white/10 text-[#F2C46A]",
      hint: "text-emerald-100/70",
    },
    success: {
      wrap: "bg-white border-slate-200 hover:border-emerald-300",
      label: "text-slate-500",
      value: "text-slate-900",
      iconBox: "bg-emerald-50 text-emerald-700",
      hint: "text-slate-500",
    },
    danger: {
      wrap: "bg-[#FEF2F2] border-rose-200 hover:border-rose-300",
      label: "text-rose-700/80",
      value: "text-rose-700",
      iconBox: "bg-white text-rose-500",
      hint: "text-rose-700/70",
    },
    neutral: {
      wrap: "bg-white border-slate-200 hover:border-slate-300",
      label: "text-slate-500",
      value: "text-slate-900",
      iconBox: "bg-slate-100 text-slate-700",
      hint: "text-slate-500",
    },
  };
  const s = styles[variant] || styles.default;
  const clickable = typeof onClick === "function";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!clickable}
      data-testid={testId}
      className={`text-left rounded-xl border px-4 py-3 min-h-[128px] flex flex-col justify-between transition-all duration-200 ${s.wrap} ${clickable ? "cursor-pointer hover:-translate-y-[1px]" : "cursor-default"}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className={`text-[10px] uppercase tracking-[0.14em] font-semibold leading-tight ${s.label}`}>{label}</div>
        {Icon && (
          <span className={`h-8 w-8 rounded-lg inline-flex items-center justify-center shrink-0 ${s.iconBox}`}>
            <Icon className="h-4 w-4" strokeWidth={2} />
          </span>
        )}
      </div>
      <div>
        <div className={`font-display text-[34px] font-bold leading-none tabular-nums ${s.value}`}>{value}</div>
        {trend && <div className="mt-1">{trend}</div>}
        {hint && <div className={`mt-1.5 text-[11px] leading-tight ${s.hint}`}>{hint}</div>}
      </div>
    </button>
  );
}

function Panel({ icon: Icon, title, right, children, testId }) {
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4" data-testid={testId}>
      <header className="flex items-center gap-2 mb-3">
        {Icon && (
          <span className="h-7 w-7 rounded-lg bg-[#F5F1E6] text-[#8A6B2B] inline-flex items-center justify-center">
            <Icon className="h-3.5 w-3.5" />
          </span>
        )}
        <h3 className="font-display text-[15px] font-semibold text-slate-800 tracking-tight">{title}</h3>
        {right && <div className="ml-auto text-[11px] text-slate-400">{right}</div>}
      </header>
      {children}
    </section>
  );
}

function HorizontalBar({ label, value, total, color = "#065F46", showPct = true, right, testId }) {
  const pct = total > 0 ? (value / total) * 100 : 0;
  return (
    <div className="grid grid-cols-[minmax(120px,1.3fr)_2fr_auto_auto] items-center gap-3 py-1" data-testid={testId}>
      <div className="text-[12px] text-slate-700 truncate">{label}</div>
      <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${Math.min(100, pct)}%`, background: color }} />
      </div>
      <div className="text-[12.5px] font-semibold text-slate-900 tabular-nums text-right w-8">{nfmt(value)}</div>
      {showPct && <div className="text-[11px] text-slate-500 tabular-nums w-12 text-right">{pct.toFixed(1)}%</div>}
      {right}
    </div>
  );
}

// ---------------------------------------------------------------------------
//  Dashboard
// ---------------------------------------------------------------------------
export default function DashboardPage() {
  const { companyParam, selectedCompany } = useCompany();
  const [data, setData] = useState(null);
  const [prev, setPrev] = useState(null);
  const [evolution, setEvolution] = useState(null);
  const [evoMonths, setEvoMonths] = useState(12);
  const [timeline, setTimeline] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [goals, setGoals] = useState([]);
  const [realized, setRealized] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [drill, setDrill] = useState({ open: false, category: null, key: null, title: "" });

  // Dataload principal — todos os endpoints necessários da Fase 4B
  const [reloadTick, setReloadTick] = useState(0);
  useEffect(() => {
    const onBulk = () => setReloadTick((t) => t + 1);
    window.addEventListener("bulk-employees-updated", onBulk);
    return () => window.removeEventListener("bulk-employees-updated", onBulk);
  }, []);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    const params = companyParam ? { company_id: companyParam } : {};
    const prevParams = { ...params, period: prevPeriodStr() };
    Promise.all([
      api.get("/headcount/summary", { params }),
      api.get("/headcount/summary", { params: prevParams }).catch(() => ({ data: null })),
      api.get("/headcount/timeline", { params: { ...params, months: 12 } }).catch(() => ({ data: null })),
      api.get("/dashboard/alerts",  { params }).catch(() => ({ data: null })),
      api.get("/dashboard/goals",   { params }).catch(() => ({ data: { goals: [] } })),
      api.get("/dashboard/realized",{ params }).catch(() => ({ data: null })),
    ]).then(([s, p, tl, al, gl, rz]) => {
      if (cancel) return;
      setData(s.data);
      setPrev(p.data);
      setTimeline(tl.data);
      setAlerts(al.data);
      setGoals(gl.data?.goals || []);
      setRealized(rz.data);
      // Fire-and-forget: verifica alertas para eventual notificação preventiva.
      // Idempotente no backend — só dispara e-mail em transição Normal→Crítico.
      api.post("/dashboard/alerts/check", null, { params })
        .catch(() => { /* silencioso — endpoint é acessório */ });
    }).finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [companyParam, reloadTick]);

  // Evolução mensal (recalcula sem reload total quando o toggle 3M/6M/12M muda)
  useEffect(() => {
    let cancel = false;
    const params = companyParam ? { company_id: companyParam } : {};
    api.get("/headcount/evolution", { params: { ...params, months: evoMonths } })
      .then(({ data }) => { if (!cancel) setEvolution(data); })
      .catch(() => { if (!cancel) setEvolution(null); });
    return () => { cancel = true; };
  }, [companyParam, evoMonths, reloadTick]);

  const openDrill = (category, key, title) => setDrill({ open: true, category, key, title });
  const closeDrill = () => setDrill({ ...drill, open: false });

  const byKey = useMemo(() => {
    const m = {};
    (data?.categorias_vinculo || []).forEach((c) => { m[c.key] = c; });
    return m;
  }, [data]);

  const clt        = byKey.CLT        || { total: 0, ativos: 0, afastados: 0 };
  const totCLT     = clt.total || 0;
  const totAprend  = byKey.Aprendiz?.total   || 0;
  const totTempor  = byKey.Temporario?.total || 0;
  const totPJ      = byKey.PJ?.total         || 0;
  const totAutonomo= byKey.Autonomo?.total   || 0;
  const totEstagio = byKey.Estagio?.total    || 0;

  // Regras fixas de negócio (preservadas):
  //   Total de Colaboradores = CLT (Ativos + Afastados) + Aprendiz + Temporário — sem PJ
  //   Total de Mão de Obra   = Total de Colaboradores + PJ (+ Autônomo se existir na base)
  const totalColaboradores = totCLT + totAprend + totTempor;
  const totalMaoDeObra     = totalColaboradores + totPJ + totAutonomo;

  // Comparação com mês anterior (Tendências)
  const prevByKey = useMemo(() => {
    const m = {};
    (prev?.categorias_vinculo || []).forEach((c) => { m[c.key] = c; });
    return m;
  }, [prev]);
  const prevTotalColab = (prevByKey.CLT?.total || 0)
                       + (prevByKey.Aprendiz?.total || 0)
                       + (prevByKey.Temporario?.total || 0);
  const prevTotalMaoDeObra = prevTotalColab + (prevByKey.PJ?.total || 0) + (prevByKey.Autonomo?.total || 0);

  const admitidos = data?.movimentacao_mes?.admitidos ?? 0;
  const demitidos = data?.movimentacao_mes?.demitidos ?? 0;
  const saldo     = data?.movimentacao_mes?.saldo ?? (admitidos - demitidos);

  // Donut composição (SEM PJ — 464 colaboradores)
  const composicao = useMemo(() => {
    const items = [
      { name: "CLT Ativos",    value: clt.ativos,    color: "#065F46" },
      { name: "CLT Afastados", value: clt.afastados, color: "#F59E0B" },
      { name: "Aprendiz",      value: totAprend,     color: "#22C55E" },
      { name: "Temporário",    value: totTempor,     color: "#A855F7" },
    ];
    return items;
  }, [clt.ativos, clt.afastados, totAprend, totTempor]);

  // Tipos de vínculo (barras horizontais)
  const vinculos = useMemo(() => {
    const items = [
      { key: "CLT_ATIVOS",  label: "CLT Ativos",    value: clt.ativos,    color: "#065F46", drill: ["clt_ativos", null] },
      { key: "CLT_AFAST",   label: "CLT Afastados", value: clt.afastados, color: "#F59E0B", drill: ["clt_afastados", null] },
      { key: "APRENDIZ",    label: "Aprendiz",      value: totAprend,     color: "#0EA5E9", drill: ["vinculo", "Aprendiz"] },
      { key: "TEMPORARIO",  label: "Temporário",    value: totTempor,     color: "#A855F7", drill: ["vinculo", "Temporario"] },
    ];
    if (totEstagio > 0)  items.push({ key: "ESTAGIO",  label: "Estágio",  value: totEstagio,  color: "#6366F1", drill: ["vinculo", "Estagio"] });
    if (totAutonomo > 0) items.push({ key: "AUTONOMO", label: "Autônomo", value: totAutonomo, color: "#8A6B2B", drill: ["vinculo", "Autonomo"] });
    return items;
  }, [clt.ativos, clt.afastados, totAprend, totTempor, totEstagio, totAutonomo]);
  const vinculosMax = Math.max(1, ...vinculos.map((v) => v.value));

  // Evolução mensal
  const evolutionData = useMemo(() => {
    if (!evolution?.months) return [];
    return evolution.months.map((m, i) => ({
      month: m,
      Admitidos: evolution.series?.[0]?.data?.[i] ?? 0,
      Demitidos: evolution.series?.[1]?.data?.[i] ?? 0,
    }));
  }, [evolution]);

  // Afastados por motivo
  const afastTotal = data?.afastamentos?.total || 0;
  const afastByType = useMemo(() => {
    const arr = (data?.afastamentos?.by_type || []).map((t) => ({
      key: t.tipo,
      label: LEAVE_TYPE_LABEL[t.tipo] || t.tipo,
      value: t.total,
      color: t.tipo === "TIPO_NAO_INFORMADO" ? "#F59E0B" : "#065F46",
    }));
    return arr.slice(0, 6);
  }, [data]);

  // Afastados por tempo (bucket)
  const afastByBucket = useMemo(() => {
    if (!data?.afastamentos?.by_bucket) return [];
    return BUCKETS
      .map((b) => ({ key: b.key, label: b.label, value: data.afastamentos.by_bucket[b.key] || 0, color: b.color }))
      .filter((b) => b.value > 0);
  }, [data]);

  const doExport = async (kind) => {
    setExporting(true);
    try {
      const companyLabel = selectedCompany?.nome_fantasia || selectedCompany?.razao_social || "Grupo ALCON";
      const periodLabel = `${data?.period?.start || ""} → ${data?.period?.end_exclusive || ""}`;
      if (kind === "png") await exportDashboardPng({ companyLabel });
      else await exportDashboardPdf({ companyLabel, periodLabel });
      toast.success(`Snapshot ${kind.toUpperCase()} gerado.`);
    } catch (e) {
      toast.error(`Falha ao exportar: ${e.message || e}`);
    } finally { setExporting(false); }
  };

  return (
    <div className="space-y-4 pb-8" data-testid="dashboard-page">
      {/* Cabeçalho do Dashboard com botão de export (fora do snapshot) */}
      <header className="flex items-center justify-between flex-wrap gap-2" data-snapshot-hide>
        <div>
          <div className="text-[10.5px] uppercase tracking-[0.18em] text-[#8A6B2B] font-semibold">
            Centro de Inteligência de RH
          </div>
          <h1 className="font-display text-[22px] font-semibold text-slate-900 leading-tight">
            {selectedCompany?.nome_fantasia || selectedCompany?.razao_social || "Grupo ALCON · Consolidado"}
          </h1>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              size="sm" variant="outline"
              disabled={loading || exporting}
              className="h-9 border-slate-300"
              data-testid="btn-export-snapshot"
            >
              <Download className="h-4 w-4 mr-1.5" />
              {exporting ? "Exportando..." : "Exportar snapshot"}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuItem onClick={() => doExport("pdf")} data-testid="export-pdf">
              <FileText className="h-4 w-4 mr-2 text-rose-600" />
              Exportar em PDF (A4 paisagem)
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => doExport("png")} data-testid="export-png">
              <FileImage className="h-4 w-4 mr-2 text-[#065F46]" />
              Exportar em PNG (alta resolução)
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </header>

      {/* Conteúdo capturável do snapshot */}
      <div id="dashboard-snapshot" className="space-y-4">

      {/* ---- Linha 1 · 6 KPI cards ---- */}
      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3">
        {loading || !data ? (
          [1,2,3,4,5,6].map((i) => <Skeleton key={i} className="h-[128px] rounded-xl" />)
        ) : (
          <>
            <KpiCard
              testId="kpi-total-colaboradores"
              label="Total de Colaboradores"
              value={nfmt(totalColaboradores)}
              hint={`CLT (${totCLT}) · Aprendiz (${totAprend}) · Temporário (${totTempor}) — inclui afastados`}
              icon={Users}
              variant="default"
              trend={prev ? <TrendBadge current={totalColaboradores} previous={prevTotalColab} /> : null}
              onClick={() => openDrill("vinculo_group", "TOTAL_CLT", "Total de Colaboradores")}
            />
            <KpiCard
              testId="kpi-pj"
              label="Prestadores (PJ)"
              value={nfmt(totPJ)}
              hint="Pessoa jurídica"
              icon={Briefcase}
              variant="accent"
              onClick={() => openDrill("vinculo", "PJ", "Prestadores (PJ)")}
            />
            <KpiCard
              testId="kpi-mao-de-obra"
              label="Total de Mão de Obra"
              value={nfmt(totalMaoDeObra)}
              hint={`Colaboradores (${totalColaboradores}) + PJ (${totPJ})${totAutonomo ? ` + Autônomo (${totAutonomo})` : ""}`}
              icon={UsersRound}
              variant="hero"
              trend={prev ? <TrendBadge current={totalMaoDeObra} previous={prevTotalMaoDeObra} light /> : null}
              onClick={() => openDrill("vinculo_group", "TOTAL_MAO_DE_OBRA", "Total de Mão de Obra")}
            />
            <KpiCard
              testId="kpi-admitidos-mes"
              label="Admitidos no mês"
              value={nfmt(admitidos)}
              hint={`${data?.period?.start || ""} → ${data?.period?.end_exclusive || ""}`}
              icon={UserPlus}
              variant="success"
              onClick={() => openDrill("admitidos_mes", null, "Admitidos no mês")}
            />
            <KpiCard
              testId="kpi-demitidos-mes"
              label="Demitidos no mês"
              value={nfmt(demitidos)}
              hint="Baseado em data_desligamento"
              icon={UserMinus}
              variant="danger"
              onClick={() => openDrill("demitidos_mes", null, "Demitidos no mês")}
            />
            <KpiCard
              testId="kpi-saldo-mes"
              label="Saldo do mês"
              value={saldo >= 0 ? `+${nfmt(saldo)}` : nfmt(saldo)}
              hint="Admitidos − Demitidos"
              icon={saldo >= 0 ? TrendingUp : TrendingDown}
              variant={saldo >= 0 ? "success" : "danger"}
            />
          </>
        )}
      </div>

      {/* ---- Linha 2 · Donut · Evolução · Vínculos ---- */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {/* Donut · Composição */}
        <Panel icon={UsersRound} title="Colaboradores por forma de contratação" testId="chart-composicao">
          {loading || !data ? (
            <Skeleton className="h-[240px]" />
          ) : (
            <div className="grid grid-cols-[1fr_1fr] items-center gap-2">
              <div className="relative">
                <ResponsiveContainer width="100%" height={230}>
                  <PieChart>
                    <Pie
                      data={composicao.filter((c) => c.value > 0)}
                      dataKey="value" nameKey="name"
                      cx="50%" cy="50%" innerRadius={58} outerRadius={92}
                      paddingAngle={2} stroke="#fff" strokeWidth={2}
                      labelLine={false}
                    >
                      {composicao.map((d) => <Cell key={d.name} fill={d.color} />)}
                    </Pie>
                    <Tooltip formatter={(v, n) => [`${nfmt(v)} pessoa(s)`, n]}
                             contentStyle={{ borderRadius: 8, fontSize: 12, border: "1px solid #E5E7EB" }} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                  <div className="font-display text-[26px] font-bold text-[#0F172A] leading-none tabular-nums">{nfmt(totalColaboradores)}</div>
                  <div className="mt-1 text-[10.5px] text-slate-500 leading-tight text-center">
                    Colaboradores<br/>(sem PJ)
                  </div>
                </div>
              </div>
              <ul className="space-y-1.5">
                {composicao.map((c) => {
                  const pct = totalColaboradores > 0 ? (c.value / totalColaboradores) * 100 : 0;
                  return (
                    <li key={c.name} className="grid grid-cols-[10px_1fr_auto_auto] items-center gap-2 text-[12px]">
                      <span className="h-2.5 w-2.5 rounded-full" style={{ background: c.color }} />
                      <span className="text-slate-700 truncate">{c.name}</span>
                      <span className="font-semibold text-slate-900 tabular-nums">{nfmt(c.value)}</span>
                      <span className="text-slate-500 tabular-nums w-10 text-right">{pct.toFixed(1)}%</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </Panel>

        {/* Evolução */}
        <Panel
          icon={TrendingUp}
          title="Evolução de admissões e demissões"
          right={
            <div className="inline-flex rounded-md border border-slate-200 overflow-hidden">
              {[3, 6, 12].map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setEvoMonths(m)}
                  className={`px-2 py-0.5 text-[11px] font-semibold transition-colors ${
                    evoMonths === m ? "bg-[#065F46] text-white" : "bg-white text-slate-600 hover:bg-slate-50"
                  }`}
                  data-testid={`evo-toggle-${m}m`}
                >
                  {m}M
                </button>
              ))}
            </div>
          }
          testId="chart-evolucao"
        >
          {loading || !evolution ? (
            <Skeleton className="h-[240px]" />
          ) : evolutionData.length === 0 ? (
            <div className="h-[240px] flex items-center justify-center text-[12px] text-slate-500">
              Sem dados no escopo selecionado.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={evolutionData} margin={{ top: 8, right: 8, left: -12, bottom: 0 }} barCategoryGap="18%">
                <CartesianGrid stroke="#F1F5F9" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#64748B" }} axisLine={{ stroke: "#E5E7EB" }} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip
                  formatter={(v, n) => [`${nfmt(v)} pessoa(s)`, n]}
                  labelFormatter={(l, payload) => {
                    if (!payload?.[0]) return l;
                    const row = payload[0].payload;
                    const s = (row?.Admitidos || 0) - (row?.Demitidos || 0);
                    const sig = s > 0 ? "+" : "";
                    return `${l} · Saldo ${sig}${s}`;
                  }}
                  contentStyle={{ borderRadius: 8, fontSize: 12, border: "1px solid #E5E7EB" }}
                />
                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11, paddingTop: 4 }} />
                <Bar dataKey="Admitidos" fill="#065F46" radius={[3, 3, 0, 0]} />
                <Bar dataKey="Demitidos" fill="#EF4444" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Panel>

        {/* Vínculos horizontais */}
        <Panel icon={Users} title="Colaboradores por tipo de vínculo" testId="chart-vinculos">
          {loading || !data ? (
            <Skeleton className="h-[240px]" />
          ) : (
            <div className="space-y-1.5">
              {vinculos.map((v) => (
                <button
                  key={v.key}
                  type="button"
                  onClick={() => openDrill(...v.drill, v.label)}
                  className="w-full text-left grid grid-cols-[minmax(110px,1fr)_2fr_auto] items-center gap-3 py-1.5 hover:bg-slate-50 rounded-md px-1 transition-colors"
                  data-testid={`vinculo-${v.key}`}
                >
                  <span className="text-[12px] text-slate-700 truncate">{v.label}</span>
                  <span className="h-2 rounded-full bg-slate-100 overflow-hidden">
                    <span className="block h-full rounded-full transition-all duration-500"
                          style={{ width: `${(v.value / vinculosMax) * 100}%`, background: v.color }} />
                  </span>
                  <span className="text-[13px] font-semibold text-slate-900 tabular-nums w-8 text-right">{nfmt(v.value)}</span>
                </button>
              ))}
              <div className="pt-2 mt-2 border-t border-dashed border-slate-200 text-[11px] text-slate-500 leading-relaxed">
                PJ ({totPJ}) é contabilizado como <b>força externa</b> e compõe o Total de Mão de Obra, mas não os {totalColaboradores} colaboradores.
              </div>
            </div>
          )}
        </Panel>
      </div>

      {/* ---- Linha 3 · Afastados motivo · Afastados tempo · Classificação ---- */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {/* Afastados por motivo */}
        <Panel icon={UserRoundX} title="Afastados por tipo de motivo"
               right={`Total ${nfmt(afastTotal)}`} testId="chart-afast-motivo">
          {loading || !data ? (
            <Skeleton className="h-[240px]" />
          ) : afastByType.length === 0 ? (
            <div className="h-[220px] flex items-center justify-center text-[12px] text-slate-500">Sem afastamentos no escopo.</div>
          ) : (
            <div className="space-y-0.5">
              {afastByType.map((a) => (
                <button
                  key={a.key}
                  type="button"
                  onClick={() => openDrill("afastamentos_tipo", a.key, a.label)}
                  className="w-full text-left hover:bg-slate-50 rounded-md px-1 transition-colors"
                  data-testid={`afast-motivo-${a.key}`}
                >
                  <HorizontalBar label={a.label} value={a.value} total={afastTotal} color={a.color} />
                </button>
              ))}
            </div>
          )}
        </Panel>

        {/* Afastados por tempo */}
        <Panel icon={Clock} title="Afastados por tempo de afastamento" testId="chart-afast-tempo">
          {loading || !data ? (
            <Skeleton className="h-[240px]" />
          ) : afastByBucket.length === 0 ? (
            <div className="h-[220px] flex items-center justify-center text-[12px] text-slate-500">Sem afastamentos no escopo.</div>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={afastByBucket} margin={{ top: 16, right: 8, left: -20, bottom: 20 }} barCategoryGap="20%">
                <CartesianGrid stroke="#F1F5F9" vertical={false} />
                <XAxis
                  dataKey="label" tick={{ fontSize: 10, fill: "#64748B" }}
                  axisLine={{ stroke: "#E5E7EB" }} tickLine={false} interval={0}
                />
                <YAxis tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip
                  formatter={(v) => [`${nfmt(v)} pessoa(s)`, "Afastados"]}
                  contentStyle={{ borderRadius: 8, fontSize: 12, border: "1px solid #E5E7EB" }}
                />
                <Bar dataKey="value" radius={[4, 4, 0, 0]}
                     label={{ position: "top", fontSize: 11, fill: "#334155", fontWeight: 600 }}>
                  {afastByBucket.map((b) => <Cell key={b.key} fill={b.color} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Panel>

        {/* Força de trabalho por classificação */}
        <Panel icon={Factory} title="Força de trabalho por classificação" testId="chart-forca-trabalho">
          {loading || !data ? (
            <Skeleton className="h-[240px]" />
          ) : (
            <div className="grid grid-cols-2 gap-2">
              {(data?.tipo_operacional || []).map((t) => {
                const meta = OP_META[t.key] || { icon: Users, label: t.label };
                const Icon = meta.icon;
                const totalOp = (data?.tipo_operacional || []).reduce((s, x) => s + (x.total || 0), 0);
                const pct = totalOp > 0 ? (t.total / totalOp) * 100 : 0;
                return (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => openDrill("tipo_operacional", t.key, t.label)}
                    className="text-left rounded-lg border border-slate-200 bg-white p-3 hover:border-[#065F46]/40 hover:-translate-y-[1px] transition-all"
                    data-testid={`forca-${t.key}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="h-6 w-6 rounded-md bg-[#F5F1E6] text-[#8A6B2B] inline-flex items-center justify-center">
                        <Icon className="h-3.5 w-3.5" />
                      </span>
                      <ChevronRight className="h-3.5 w-3.5 text-slate-300" />
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold">{meta.label}</div>
                    <div className="font-display text-[24px] font-bold text-slate-900 tabular-nums leading-tight">{nfmt(t.total)}</div>
                    <div className="mt-1.5 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                      <div className="h-full rounded-full bg-[#065F46]" style={{ width: `${pct}%` }} />
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </Panel>
      </div>

      {/* ---- FASE 4B · Linha 4 · Alertas Inteligentes + Turnover ---- */}
      {alerts && (
        <section data-testid="row-alerts">
          <div className="mb-2 flex items-center gap-2">
            <ShieldAlert className="h-4 w-4 text-[#065F46]" />
            <h2 className="font-display text-[15px] font-semibold text-slate-800 tracking-tight">
              Alertas Inteligentes · Semáforo Gerencial
            </h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-3">
            <SemaphoreCard label="Setores acima do quadro" value={alerts.summary?.critico || 0}
                           tone="critico" icon={AlertTriangle} testId="alert-critico" />
            <SemaphoreCard label="Setores em atenção" value={alerts.summary?.atencao || 0}
                           tone="atencao" icon={Activity} testId="alert-atencao"
                           hint={`≥ ${Math.round((alerts.params?.warn_ratio || 0.9) * 100)}% do quadro aprovado`} />
            <SemaphoreCard label="Setores dentro do quadro" value={alerts.summary?.normal || 0}
                           tone="normal" icon={Users} testId="alert-normal" />
            <TurnoverCard turnover={alerts.turnover} testId="alert-turnover" />
          </div>
        </section>
      )}

      {/* ---- FASE 4B · Linha 5 · Timeline CLT/PJ/Total + Heatmap Setores ---- */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <Panel icon={TrendingUp}
               title="Evolução da mão de obra — últimos 12 meses"
               right="CLT · PJ · Total"
               testId="chart-timeline">
          {loading || !timeline ? (
            <Skeleton className="h-[260px]" />
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={(timeline.months || []).map((m, i) => ({
                month: m,
                CLT:   timeline.series?.clt?.[i]   ?? 0,
                PJ:    timeline.series?.pj?.[i]    ?? 0,
                Total: timeline.series?.total?.[i] ?? 0,
              }))} margin={{ top: 8, right: 12, left: -12, bottom: 0 }}>
                <CartesianGrid stroke="#F1F5F9" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 10.5, fill: "#64748B" }} axisLine={{ stroke: "#E5E7EB" }} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip formatter={(v, n) => [`${nfmt(v)}`, n]}
                         contentStyle={{ borderRadius: 8, fontSize: 12, border: "1px solid #E5E7EB" }} />
                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11, paddingTop: 4 }} />
                <Line type="monotone" dataKey="CLT"   stroke="#065F46" strokeWidth={2.4} dot={{ r: 2.5 }} activeDot={{ r: 4 }} />
                <Line type="monotone" dataKey="PJ"    stroke="#C99328" strokeWidth={2.4} dot={{ r: 2.5 }} activeDot={{ r: 4 }} />
                <Line type="monotone" dataKey="Total" stroke="#0F172A" strokeWidth={2.4} strokeDasharray="4 3" dot={{ r: 2.5 }} activeDot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </Panel>

        <Panel icon={Grid3x3}
               title="Mapa de calor do quadro por setor"
               right={alerts ? `${(alerts.sectors || []).length} setor(es)` : ""}
               testId="chart-heatmap">
          {loading || !alerts ? (
            <Skeleton className="h-[260px]" />
          ) : (alerts.sectors || []).length === 0 ? (
            <div className="h-[220px] flex items-center justify-center text-[12px] text-slate-500">Sem setores no escopo.</div>
          ) : (
            <div className="lg:col-span-2 max-h-[280px] overflow-y-auto pr-1">
              <SectorHeatmap sectors={alerts.sectors} />
            </div>
          )}
        </Panel>

        <Panel icon={Target}
               title="Metas × Realizado"
               right={goals?.length ? `${goals.length} meta(s)` : "não definidas"}
               testId="chart-goals">
          <GoalsPanel goals={goals} realized={realized?.realized} />
        </Panel>
      </div>

      </div>{/* /dashboard-snapshot */}

      <HeadcountDrillDialog
        open={drill.open}
        onClose={closeDrill}
        title={drill.title}
        category={drill.category}
        keyValue={drill.key}
        companyId={companyParam || undefined}
      />
    </div>
  );
}


// ---------------------------------------------------------------------------
//  FASE 4B · Auxiliares (Alertas · Heatmap · Metas)
// ---------------------------------------------------------------------------
function SemaphoreCard({ label, value, tone, icon: Icon, hint, testId }) {
  const tones = {
    critico:  { wrap: "bg-white border-rose-200",       ico: "bg-rose-100 text-rose-600",     val: "text-rose-700",   dot: "bg-rose-500"    },
    atencao:  { wrap: "bg-white border-amber-200",      ico: "bg-amber-100 text-amber-600",   val: "text-amber-700",  dot: "bg-amber-500"   },
    normal:   { wrap: "bg-white border-emerald-200",    ico: "bg-emerald-100 text-emerald-600", val: "text-emerald-700", dot: "bg-emerald-500" },
    info:     { wrap: "bg-white border-slate-200",      ico: "bg-slate-100 text-slate-700",   val: "text-slate-900",  dot: "bg-slate-400"   },
  };
  const s = tones[tone] || tones.info;
  return (
    <div className={`rounded-xl border px-4 py-3 min-h-[110px] ${s.wrap}`} data-testid={testId}>
      <div className="flex items-start justify-between">
        <div className="text-[10px] uppercase tracking-[0.14em] font-semibold text-slate-500 leading-tight">{label}</div>
        <span className={`h-7 w-7 rounded-lg inline-flex items-center justify-center ${s.ico}`}>
          <Icon className="h-3.5 w-3.5" />
        </span>
      </div>
      <div className="mt-2 flex items-baseline gap-2">
        <span className={`h-2 w-2 rounded-full ${s.dot}`} />
        <div className={`font-display text-[30px] font-bold tabular-nums leading-none ${s.val}`}>{(value ?? 0).toLocaleString("pt-BR")}</div>
      </div>
      {hint && <div className="mt-1.5 text-[11px] text-slate-500">{hint}</div>}
    </div>
  );
}

function TurnoverCard({ turnover, testId }) {
  if (!turnover) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 min-h-[110px]" data-testid={testId}>
        <div className="text-[10px] uppercase tracking-[0.14em] font-semibold text-slate-500">Turnover · mês atual</div>
        <div className="mt-2 font-display text-[24px] font-bold text-slate-400">—</div>
        <div className="mt-1 text-[11px] text-slate-500">sem dados</div>
      </div>
    );
  }
  const critical = turnover.status === "critico";
  const tones = critical
    ? { wrap: "bg-[#FEF2F2] border-rose-200",    val: "text-rose-700",    ico: "bg-white text-rose-500",    dot: "bg-rose-500"    }
    : { wrap: "bg-white border-emerald-200",     val: "text-emerald-700", ico: "bg-emerald-100 text-emerald-600", dot: "bg-emerald-500" };
  return (
    <div className={`rounded-xl border px-4 py-3 min-h-[110px] ${tones.wrap}`} data-testid={testId}>
      <div className="flex items-start justify-between">
        <div className="text-[10px] uppercase tracking-[0.14em] font-semibold text-slate-500 leading-tight">Turnover · {turnover.period}</div>
        <span className={`h-7 w-7 rounded-lg inline-flex items-center justify-center ${tones.ico}`}>
          <Gauge className="h-3.5 w-3.5" />
        </span>
      </div>
      <div className="mt-2 flex items-baseline gap-2">
        <span className={`h-2 w-2 rounded-full ${tones.dot}`} />
        <div className={`font-display text-[30px] font-bold tabular-nums leading-none ${tones.val}`}>
          {(turnover.pct ?? 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%
        </div>
      </div>
      <div className="mt-1.5 text-[11px] text-slate-500 leading-tight">
        Limite {turnover.limit}% · {turnover.demitidos} deslig. / HC médio {Math.round(turnover.headcount_medio || 0)}
        {critical && <span className="ml-1 font-semibold text-rose-700">· acima do limite</span>}
      </div>
    </div>
  );
}

function SectorHeatmap({ sectors }) {
  // Colore por intensidade de desvio (%)
  const colorFor = (row) => {
    if (row.status === "sem_quadro") return { bg: "#F1F5F9", fg: "#64748B" };
    if (row.status === "critico") {
      // vermelho escala pelo % de excesso
      const excess = row.quadro_aprovado > 0 ? (row.diferenca / row.quadro_aprovado) : 0;
      if (excess > 0.25) return { bg: "#DC2626", fg: "#FFFFFF" };
      if (excess > 0.10) return { bg: "#EF4444", fg: "#FFFFFF" };
      return { bg: "#FCA5A5", fg: "#7F1D1D" };
    }
    if (row.status === "atencao") return { bg: "#FDE68A", fg: "#92400E" };
    // normal: verde discreto
    return { bg: "#D1FAE5", fg: "#065F46" };
  };
  return (
    <div className="space-y-1">
      {sectors.slice(0, 40).map((s) => {
        const c = colorFor(s);
        const label = s.diferenca > 0 ? `+${s.diferenca}` : s.diferenca;
        return (
          <div key={s.sector_id}
               title={`${s.name} · Previsto ${s.quadro_aprovado} · Atual ${s.atual_clt} · PJ ${s.pj}`}
               className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-2 py-1 px-1.5 rounded-md hover:bg-slate-50 transition-colors"
               data-testid={`heatmap-sector-${s.sector_id}`}>
            <span className="text-[12px] text-slate-700 truncate">{s.name}</span>
            <span className="text-[11px] text-slate-500 tabular-nums w-16 text-right">
              {s.atual_clt} / {s.quadro_aprovado}
            </span>
            <span
              className="text-[11px] font-semibold tabular-nums rounded-md px-2 py-0.5 min-w-[44px] text-center"
              style={{ background: c.bg, color: c.fg }}
            >
              {label}
            </span>
            <span className="text-[10.5px] text-slate-500 tabular-nums w-12 text-right">
              {s.ocupacao_pct !== null ? `${s.ocupacao_pct}%` : "—"}
            </span>
          </div>
        );
      })}
      {sectors.length > 40 && (
        <div className="pt-1 text-[10.5px] text-slate-400 text-center">
          Exibindo 40 de {sectors.length} setores (ordenados por desvio)
        </div>
      )}
    </div>
  );
}

const INDICATOR_LABEL = {
  turnover:    "Turnover",
  absenteismo: "Absenteísmo",
  headcount:   "Headcount",
  horas_extras:"Horas Extras",
};
const INDICATOR_ORDER = ["turnover", "absenteismo", "headcount", "horas_extras"];

function GoalsPanel({ goals, realized }) {
  // realized = { turnover: {value, unit}, absenteismo: {...}, headcount: {...}, horas_extras: {...} }
  const goalsByInd = {};
  (goals || []).forEach((g) => {
    // Prioriza meta ativa; se houver várias no mesmo indicador, prefere escopo mais específico
    if (!g.active) return;
    const prev = goalsByInd[g.indicator];
    const rank = (x) => x.scope === "setor" ? 3 : x.scope === "empresa" ? 2 : 1;
    if (!prev || rank(g) > rank(prev)) goalsByInd[g.indicator] = g;
  });

  const rows = INDICATOR_ORDER.map((ind) => {
    const g = goalsByInd[ind];
    const r = realized?.[ind];
    const rvalue = r?.value ?? null;
    const runit  = r?.unit  ?? (ind === "headcount" ? "qtd" : ind === "horas_extras" ? "h" : "%");
    return {
      key: ind,
      label: INDICATOR_LABEL[ind],
      unit: g?.unit || runit,
      target: g?.target ?? null,
      operator: g?.operator || "<=",
      realized: rvalue,
      note: r?.note,
      compare: (g?.unit === "%" || runit === "%") ? "pp" : "abs",
    };
  });

  return (
    <div className="space-y-2" data-testid="goals-panel">
      {rows.map((r) => {
        const hasTarget = r.target !== null && r.target !== undefined;
        const hasReal   = r.realized !== null && r.realized !== undefined;
        let status = "info";
        let deltaTxt = null;
        if (hasTarget && hasReal) {
          const delta = r.realized - r.target;
          const bad = r.operator === "<=" ? delta > 0
                    : r.operator === ">=" ? delta < 0
                    : Math.abs(delta) > 0.05;
          status = bad ? "critico" : "normal";
          const sign = delta > 0 ? "+" : "";
          const suf = r.compare === "pp" ? " p.p." : ` ${r.unit || ""}`.trimEnd();
          deltaTxt = `${sign}${Number(delta).toFixed(r.compare === "pp" ? 2 : 0)}${suf}`;
        }
        const dot = status === "critico" ? "bg-rose-500"
                  : status === "normal"  ? "bg-emerald-500"
                  : "bg-slate-300";
        const targetTxt = hasTarget
          ? `${r.operator} ${r.target}${r.unit === "%" ? "%" : ` ${r.unit || ""}`.trimEnd()}`
          : null;
        const realizedTxt = hasReal
          ? `${Number(r.realized).toLocaleString("pt-BR", {
              minimumFractionDigits: r.unit === "%" ? 2 : 0,
              maximumFractionDigits: r.unit === "%" ? 2 : 0,
            })}${r.unit === "%" ? "%" : ""}`
          : null;
        return (
          <div key={r.key} className="rounded-lg border border-slate-200 bg-white p-2.5"
               data-testid={`goal-${r.key}`}>
            <div className="flex items-center justify-between">
              <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-600">{r.label}</div>
              <span className={`h-2 w-2 rounded-full ${dot}`} />
            </div>
            <div className="mt-1 grid grid-cols-3 gap-2 items-baseline">
              <div>
                <div className="text-[9.5px] uppercase tracking-wider text-slate-400">Meta</div>
                <div className="text-[13px] font-semibold text-slate-800 tabular-nums">
                  {targetTxt || <span className="text-slate-400 italic text-[11.5px]">não definida</span>}
                </div>
              </div>
              <div>
                <div className="text-[9.5px] uppercase tracking-wider text-slate-400">Realizado</div>
                <div className="text-[13px] font-semibold text-slate-900 tabular-nums">
                  {realizedTxt || <span className="text-slate-300">—</span>}
                </div>
              </div>
              <div>
                <div className="text-[9.5px] uppercase tracking-wider text-slate-400">Desvio</div>
                <div className={`text-[13px] font-semibold tabular-nums ${status === "critico" ? "text-rose-700" : status === "normal" ? "text-emerald-700" : "text-slate-400"}`}>
                  {deltaTxt || "—"}
                </div>
              </div>
            </div>
            {r.note && !hasReal && (
              <div className="mt-1 text-[10.5px] text-amber-700">{r.note}</div>
            )}
          </div>
        );
      })}
      {(!goals || goals.length === 0) && (
        <div className="text-[10.5px] text-slate-400 text-center pt-1">
          Configure metas em <b>Admin → Metas do Grupo</b> (<code className="text-[10px]">/admin/metas</code>)
        </div>
      )}
    </div>
  );
}
